import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { useCurrency } from "../context/CurrencyContext";
import { MagnifyingGlass, ShoppingBag, SortAscending } from "@phosphor-icons/react";
import { CategoryIcon } from "../components/CategoryIcons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShopPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeCategory = searchParams.get("category") || "All";
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("newest");
  const { addItem } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    axios.get(`${API}/categories`).then((res) => setCategories(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    if (activeCategory !== "All") params.set("category", activeCategory);
    if (searchQuery) params.set("search", searchQuery);
    if (sortBy) params.set("sort", sortBy);

    axios
      .get(`${API}/products?${params.toString()}`)
      .then((res) => setProducts(res.data))
      .catch(console.error);
  }, [activeCategory, searchQuery, sortBy]);

  const handleCategoryChange = (cat) => {
    if (cat === "All") {
      setSearchParams({});
    } else {
      setSearchParams({ category: cat });
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div data-testid="shop-page" className="pt-20 md:pt-24 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 md:px-12">
        {/* Header */}
        <div className="py-6 md:py-10">
          <h1
            className="text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-[#1A1A1A]"
            style={{ fontFamily: "'Lazy Dog', cursive" }}
          >
            shop
          </h1>
        </div>

        {/* Search + Sort Row */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <MagnifyingGlass
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]"
            />
            <input
              data-testid="search-input"
              type="text"
              placeholder="search products..."
              value={searchQuery}
              onChange={handleSearch}
              className="w-full pl-9 pr-4 py-2 bg-[#EBD988] border border-[#D4C470] rounded-full text-xs sm:text-sm text-[#1A1A1A] placeholder:text-[#555] focus:outline-none focus:border-[#E01A23] transition-colors"
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger
                data-testid="sort-select"
                className="bg-[#EBD988] border-[#D4C470] rounded-full text-sm h-[42px]"
              >
                <SortAscending size={16} className="mr-2 text-[#3a3a3a]" />
                <SelectValue placeholder="sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#F5E6A0] border-[#D4C470]">
                <SelectItem value="newest">newest</SelectItem>
                <SelectItem value="price_asc">price: low to high</SelectItem>
                <SelectItem value="price_desc">price: high to low</SelectItem>
                <SelectItem value="name_asc">name: a-z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-3 mb-6" data-testid="category-filters">
          {categories.map((cat) => (
            <button
              key={cat}
              data-testid={`category-filter-${cat.toLowerCase().replace(/\s/g, '-')}`}
              onClick={() => handleCategoryChange(cat)}
              className={`text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded-full transition-all duration-300 flex items-center gap-1 ${
                activeCategory === cat
                  ? "bg-[#E01A23] text-white"
                  : "bg-[#EBD988] text-[#3a3a3a] hover:text-[#1A1A1A] hover:bg-[#e0d7c8]"
              }`}
              style={{ fontFamily: "'Courier New', Courier, monospace" }}
            >
              {cat !== "All" && <CategoryIcon category={cat} size={16} className={activeCategory === cat ? "text-white" : "text-[#E01A23]"} />}
              {cat.toLowerCase()}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-sm text-[#555] mb-6" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
          {products.length} result{products.length !== 1 ? "s" : ""}
        </p>

        {/* Product Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-8 pb-12">
          {products.map((product, index) => (
            <div
              key={product.id}
              data-testid={`product-card-${product.id}`}
              className={`group opacity-0 animate-fade-in-up stagger-${(index % 5) + 1}`}
            >
              <Link to={`/product/${product.id}`}>
                <div className="overflow-hidden rounded-xl aspect-square bg-[#EBD988] relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  {/* Quick add */}
                  <div className="absolute inset-x-0 bottom-0 p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                    <button
                      data-testid={`add-to-cart-button-${product.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        addItem(product);
                      }}
                      className="w-full flex items-center justify-center gap-2 bg-[#E01A23] hover:bg-[#c4161e] text-white text-xs py-2.5 rounded-full transition-all duration-300"
                      style={{ fontFamily: "'Courier New', Courier, monospace" }}
                    >
                      <ShoppingBag size={14} weight="bold" />
                      add to cart
                    </button>
                  </div>
                </div>
              </Link>
              <div className="mt-3">
                <Link to={`/product/${product.id}`}>
                  <h3
                    className="text-sm text-[#1A1A1A] hover:text-[#E01A23] transition-colors"
                    style={{ fontFamily: "'Courier New', Courier, monospace" }}
                  >
                    {product.name}
                  </h3>
                </Link>
                <span
                  className="text-sm text-[#3a3a3a] mt-0.5 block"
                  style={{ fontFamily: "'Courier New', Courier, monospace" }}
                >
                  {formatPrice(product.price)}
                </span>
              </div>
            </div>
          ))}
        </div>

        {products.length === 0 && (
          <div className="text-center py-24">
            <p className="text-[#555] text-lg" style={{ fontFamily: "'Courier New', Courier, monospace" }}>
              no products found
            </p>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
